/** Flatten canvas locations / events / legs into itinerary stop rows (PR #50). */

export type TripPlanBoardStop = {
  id: string;
  kind: string;
  label: string;
  address?: string;
  lat?: number;
  lng?: number;
  startAt?: string;
  endAt?: string;
  sortOrder: number;
  source: 'plan_draft';
  boardKind: 'location' | 'event' | 'leg';
  boardId: string;
};

function mapStopKind(kind?: string, label?: string): string {
  const k = String(kind || '').toLowerCase();
  const named = String(label || '').toLowerCase();
  if (k === 'origin' || k === 'home' || k === 'start' || named === 'home' || named === 'origin') {
    return 'origin';
  }
  if (k === 'destination' || k === 'jobsite' || k === 'site' || k === 'end') return 'destination';
  if (k === 'hotel' || k === 'lodge' || k === 'lodging' || k === 'stay') return 'hotel';
  if (k.includes('flight') && k.includes('dep')) return 'flight_departure';
  if (k.includes('flight') && k.includes('arr')) return 'flight_arrival';
  if (k === 'activity' || k === 'meeting' || k === 'event' || k === 'work') return 'activity';
  return 'other';
}

export function buildStopsFromTripPlanBoard(draft: {
  locations?: Array<{
    id?: string;
    label: string;
    kind?: string;
    address?: string;
    lat?: number;
    lng?: number;
  }>;
  events?: Array<{
    id?: string;
    title: string;
    locationId?: string;
    kind?: string;
    startAt?: string;
    endAt?: string;
  }>;
  legs?: Array<{
    id?: string;
    fromLocationId?: string;
    toLocationId?: string;
    mode?: string;
    label?: string;
    startAt?: string;
    endAt?: string;
  }>;
}): TripPlanBoardStop[] {
  const locations = (draft.locations || []).map((loc, index) => ({
    id: String(loc.id || `loc-${index + 1}`).trim() || `loc-${index + 1}`,
    label: loc.label,
    kind: loc.kind,
    address: loc.address,
    lat: loc.lat,
    lng: loc.lng,
  }));
  const events = (draft.events || []).map((ev, index) => ({
    id: String(ev.id || `evt-${index + 1}`).trim() || `evt-${index + 1}`,
    title: ev.title,
    locationId: ev.locationId,
    kind: ev.kind,
    startAt: ev.startAt,
    endAt: ev.endAt,
  }));
  const legs = (draft.legs || []).map((leg, index) => ({
    id: String(leg.id || `leg-${index + 1}`).trim() || `leg-${index + 1}`,
    fromLocationId: leg.fromLocationId,
    toLocationId: leg.toLocationId,
    mode: leg.mode,
    label: leg.label,
    startAt: leg.startAt,
    endAt: leg.endAt,
  }));
  if (!locations.length && !events.length && !legs.length) return [];

  const byId = new Map(locations.map(loc => [loc.id, loc]));
  const orderedIds: string[] = [];
  const seen = new Set<string>();
  const pushId = (id?: string) => {
    if (!id || seen.has(id) || !byId.has(id)) return;
    seen.add(id);
    orderedIds.push(id);
  };
  legs.forEach(leg => {
    pushId(leg.fromLocationId);
    pushId(leg.toLocationId);
  });
  locations.forEach(loc => pushId(loc.id));

  const rows: TripPlanBoardStop[] = [];
  let sortOrder = 0;

  const appendLocation = (loc: (typeof locations)[number]) => {
    rows.push({
      id: `board-loc-${loc.id}`,
      kind: mapStopKind(loc.kind, loc.label),
      label: loc.label,
      address: loc.address || loc.label,
      lat: loc.lat,
      lng: loc.lng,
      sortOrder: sortOrder,
      source: 'plan_draft',
      boardKind: 'location',
      boardId: loc.id,
    });
    sortOrder += 1;
    events
      .filter(ev => ev.locationId === loc.id)
      .forEach(ev => {
        const mapped = mapStopKind(ev.kind, ev.title);
        rows.push({
          id: `board-evt-${ev.id}`,
          kind: mapped === 'other' ? 'activity' : mapped,
          label: ev.title,
          address: loc.address || loc.label,
          lat: loc.lat,
          lng: loc.lng,
          startAt: ev.startAt,
          endAt: ev.endAt,
          sortOrder,
          source: 'plan_draft',
          boardKind: 'event',
          boardId: ev.id,
        });
        sortOrder += 1;
      });
  };

  for (let i = 0; i < orderedIds.length; i += 1) {
    const loc = byId.get(orderedIds[i]);
    if (loc) appendLocation(loc);
    const nextId = orderedIds[i + 1];
    if (!nextId) continue;
    const leg = legs.find(row => row.fromLocationId === orderedIds[i] && row.toLocationId === nextId);
    if (!leg) continue;
    const fromLabel = byId.get(leg.fromLocationId || '')?.label || leg.fromLocationId || 'Origin';
    const toLabel = byId.get(leg.toLocationId || '')?.label || leg.toLocationId || 'Destination';
    const mode = String(leg.mode || 'drive').trim() || 'drive';
    const modeLabel = mode.charAt(0).toUpperCase() + mode.slice(1);
    rows.push({
      id: `board-leg-${leg.id}`,
      kind: mode.toLowerCase().includes('flight') ? 'flight_departure' : 'other',
      label: leg.label || `${modeLabel}: ${fromLabel} → ${toLabel}`,
      startAt: leg.startAt,
      endAt: leg.endAt,
      sortOrder,
      source: 'plan_draft',
      boardKind: 'leg',
      boardId: leg.id,
    });
    sortOrder += 1;
  }

  events.forEach(ev => {
    if (ev.locationId && byId.has(ev.locationId)) return;
    const mapped = mapStopKind(ev.kind, ev.title);
    rows.push({
      id: `board-evt-${ev.id}`,
      kind: mapped === 'other' ? 'activity' : mapped,
      label: ev.title,
      startAt: ev.startAt,
      endAt: ev.endAt,
      sortOrder,
      source: 'plan_draft',
      boardKind: 'event',
      boardId: ev.id,
    });
    sortOrder += 1;
  });

  legs.forEach(leg => {
    if (rows.some(row => row.boardKind === 'leg' && row.boardId === leg.id)) return;
    const fromLabel = byId.get(leg.fromLocationId || '')?.label || leg.fromLocationId || '';
    const toLabel = byId.get(leg.toLocationId || '')?.label || leg.toLocationId || '';
    const mode = String(leg.mode || 'drive').trim() || 'drive';
    const modeLabel = mode.charAt(0).toUpperCase() + mode.slice(1);
    const fallback =
      fromLabel && toLabel
        ? `${modeLabel}: ${fromLabel} → ${toLabel}`
        : leg.label || `${modeLabel} leg`;
    rows.push({
      id: `board-leg-${leg.id}`,
      kind: mode.toLowerCase().includes('flight') ? 'flight_departure' : 'other',
      label: leg.label || fallback,
      startAt: leg.startAt,
      endAt: leg.endAt,
      sortOrder,
      source: 'plan_draft',
      boardKind: 'leg',
      boardId: leg.id,
    });
    sortOrder += 1;
  });

  return rows;
}
